
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useLoadingState, useDataAvailability } from '@/contexts/offline';
import { getCurrentDomain } from '@/utils/domainUtils';
import { offlineReferralService } from '@/services/referral/offlineReferralService';

export function useReferralCode(userId?: string) {
  const [referralCode, setReferralCode] = useState<string | null>(null);
  const [referralUrl, setReferralUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { isInitializing } = useLoadingState();
  const { hasLocalData } = useDataAvailability();

  useEffect(() => {
    const fetchReferralCode = async () => {


      if (!userId) {
        setLoading(false);
        return;
      }

      // Wait for offline initialization to complete before trying to access cached data
      if (!navigator.onLine && isInitializing) {
        // Still loading offline data, wait a bit longer
        return;
      }

      try {
        setLoading(true);
        
        // Check session storage cache first
        const cachedCode = sessionStorage.getItem(`referral_code_${userId}`);
        if (cachedCode) {
          setReferralCode(cachedCode);
          // Use getCurrentDomain with forceProduction=true to ensure we always get the production URL
          setReferralUrl(`${getCurrentDomain(true)}/?ref=${cachedCode}`);
          setLoading(false);
          return;
        }
        
        // Try offline service first, then online if needed
        if (!navigator.onLine) {
          // Offline mode - try to get from cached data
          try {
            const isAvailable = await offlineReferralService.isReferralDataAvailable(userId);

            if (isAvailable) {
              const { code, url } = await offlineReferralService.getUserReferralCode(userId);
              if (code && url) {
                setReferralCode(code);
                setReferralUrl(url);
                // Cache in session storage for faster subsequent access
                sessionStorage.setItem(`referral_code_${userId}`, code);
                setError(null); // Clear any previous errors
              } else {
                setError('Referral code not available offline. Connect to internet to access referral features.');
                setReferralCode(null);
                setReferralUrl(null);
              }
            } else {
              // If offline initialization is still in progress, don't show error yet
              if (!isInitializing && hasLocalData) {
                setError('Referral features require an internet connection for first-time setup.');
                setReferralCode(null);
                setReferralUrl(null);
              } else {
                // Still initializing or no local data yet, keep loading state
                return;
              }
            }
          } catch (offlineError) {
            if (!isInitializing && hasLocalData) {
              setError('Referral features require an internet connection');
              setReferralCode(null);
              setReferralUrl(null);
            } else {
              // Still initializing or no local data yet, keep loading state
              return;
            }
          }
        } else {
          // Online mode - fetch from database
          const { data, error } = await supabase
            .from('referral_codes')
            .select('code')
            .eq('user_id', userId)
            .maybeSingle();

          if (error) {
            throw error;
          }

          if (data?.code) {
            setReferralCode(data.code);
            // Use getCurrentDomain with forceProduction=true to ensure we always get the production URL
            setReferralUrl(`${getCurrentDomain(true)}/?ref=${data.code}`);

            // Cache the code
            sessionStorage.setItem(`referral_code_${userId}`, data.code);
          } else {
            // If no code exists, create one
            await createReferralCode(userId);
          }
        }
      } catch (err: unknown) {
        console.error('Error fetching referral code:', err);
        setError(err instanceof Error ? err.message : 'Failed to load referral code');
      } finally {
        setLoading(false);
      }
    };

    fetchReferralCode();
  }, [userId, isInitializing, hasLocalData]);

  const createReferralCode = async (userId: string) => {
    if (!navigator.onLine) {
      setError('Cannot create referral code while offline');
      return;
    }

    try {
      // Generate a unique referral code based on user ID and timestamp
      const timestamp = Date.now().toString(36);
      const randomPart = Math.random().toString(36).substring(2, 5);
      const generatedCode = `${randomPart}-${timestamp}`;

      // Insert the new code
      const { data, error } = await supabase
        .from('referral_codes')
        .insert({ user_id: userId, code: generatedCode })
        .select('code')
        .single();

      if (error) {
        throw error;
      }

      if (data) {
        setReferralCode(data.code);
        // Use getCurrentDomain with forceProduction=true to ensure we always get the production URL
        setReferralUrl(`${getCurrentDomain(true)}/?ref=${data.code}`);

        // Cache the code
        sessionStorage.setItem(`referral_code_${userId}`, data.code);
      }
    } catch (err: unknown) {
      console.error('Error creating referral code:', err);
      setError(err instanceof Error ? err.message : 'Failed to create referral code');
    }
  };

  return {
    referralCode,
    referralUrl,
    loading,
    error
  };
}
